import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store/store';
import { useGetUserQuery, useUpdateMyPreferencesMutation } from '../redux/api';
import { currentUserId } from '../utils/auth';
import { HTML_LANG, isLanguage } from '../i18n/languages';
import type { Language } from '../i18n/languages';

export type { Language };

/**
 * Owns every user-facing display preference in one place — language and dark
 * mode today — the same three ways mobileapp/src/theme/PreferencesContext.tsx
 * does:
 *
 *   1. read from local storage on first render (no async gate, no flash),
 *   2. written back to local storage immediately on change, and
 *   3. pushed to the server fire-and-forget so it follows the user to their
 *      phone and to their next browser — never awaited, so a failed request
 *      can't block the UI or lose the local choice.
 *
 * The mobile app also stores a colour scheme and notification channels through
 * the same `updateMyPreferences` mutation. Those are deliberately NOT read or
 * written here: partial updates merge server-side, so leaving them alone means
 * the webapp can't clobber a choice made on the phone.
 *
 * Mounted inside <Provider store> (see app/main.tsx) because it uses RTK Query.
 */

// Same keys the mobile app uses in MMKV. Nothing shares storage across the two,
// but keeping the names identical means one thing to look for when debugging a
// "my setting didn't stick" report.
const KEYS = {
  language: 'appLanguage',
  darkMode: 'appDarkMode',
} as const;

interface PreferencesContextValue {
  language: Language;
  darkMode: boolean;
  setLanguage: (language: Language) => void;
  setDarkMode: (on: boolean) => void;
  toggleDarkMode: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue>({
  language: 'english',
  darkMode: false,
  setLanguage: () => {},
  setDarkMode: () => {},
  toggleDarkMode: () => {},
});

// A browser with storage blocked throws on access, which is not a reason to
// fail to render.
const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / storage disabled: the choice still applies for this tab.
  }
};

const loadLanguage = (): Language => {
  const stored = readStored(KEYS.language);
  return isLanguage(stored) ? stored : 'english';
};

// Light unless explicitly turned on, matching the mobile app. The OS-level
// `prefers-color-scheme` is deliberately NOT consulted: dark mode is a stored
// account preference that follows the user between the phone and the browser,
// and seeding it from this particular machine's OS setting would mean the
// toggle disagreed with the account on some devices and not others.
const loadDarkMode = (): boolean => readStored(KEYS.darkMode) === '1';

// Keep in step with --bg in the two palettes in styles/style.css. This is the
// colour mobile browsers paint their own chrome (address bar, task switcher
// card) with, so a stale value leaves a pale bar above a dark page.
const THEME_COLOR = { light: '#eceef2', dark: '#0b1220' };

/**
 * Paints the theme onto <html> as `data-theme`, which is what the stylesheet's
 * dark palette keys off (see styles/style.css). Done imperatively rather than
 * through React so it can also run BEFORE first paint — see applyStoredTheme.
 */
const applyTheme = (dark: boolean) => {
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[dark ? 'dark' : 'light']);
};

/**
 * Called once from the entry point, before React renders (see app/main.tsx).
 * Without it the first frame paints light and then flips, which is the flash
 * every dark-mode implementation is judged on.
 */
export const applyStoredTheme = () => {
  applyTheme(loadDarkMode());
  document.documentElement.lang = HTML_LANG[loadLanguage()];
};

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(loadLanguage);
  const [darkMode, setDarkModeState] = useState<boolean>(loadDarkMode);

  // `auth.userId` is only populated by setAuth, which the app never dispatches
  // — the id has to come out of the access token (same reasoning as
  // screens/admin/profile). Recomputed whenever the token changes so signing
  // in, switching organization and signing out all re-run the hydration below.
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const userId = useMemo(() => (accessToken ? currentUserId() : null), [accessToken]);

  const { data: me } = useGetUserQuery(userId ?? '', { skip: !userId });
  const [updateMyPreferences] = useUpdateMyPreferencesMutation();

  // Apply the server's copy once per sign-in. Guarded so it can't fight the
  // user: if they change a setting while the query is refetching, the local
  // value stands.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!userId) {
      hydratedFor.current = null; // next user hydrates from their own record
      return;
    }
    if (hydratedFor.current === userId || !me?.preferences) return;
    hydratedFor.current = userId;

    const { language: storedLanguage, darkMode: storedDarkMode } = me.preferences;

    if (isLanguage(storedLanguage)) {
      setLanguageState(storedLanguage);
      writeStored(KEYS.language, storedLanguage);
    }
    if (typeof storedDarkMode === 'boolean') {
      setDarkModeState(storedDarkMode);
      writeStored(KEYS.darkMode, storedDarkMode ? '1' : '0');
    }
  }, [userId, me]);

  // Keep the document in step with the choice. applyStoredTheme has already
  // done this for the first paint; these cover every change after it.
  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[language];
  }, [language]);

  // Best-effort account sync. The local write has already happened by the time
  // this runs, so an offline or rejected request costs nothing but the
  // cross-device sync.
  const push = useCallback(
    (patch: { language?: Language; darkMode?: boolean }) => {
      // Signed out — there is no account to write to, so the local copy is the
      // whole story until they sign in (at which point their account's choice
      // takes over, via the hydration above).
      if (!currentUserId()) return;

      updateMyPreferences(patch)
        .unwrap()
        .catch(() => undefined);
    },
    [updateMyPreferences],
  );

  const setLanguage = useCallback(
    (next: Language) => {
      setLanguageState(next);
      writeStored(KEYS.language, next);
      push({ language: next });
    },
    [push],
  );

  const setDarkMode = useCallback(
    (on: boolean) => {
      setDarkModeState(on);
      writeStored(KEYS.darkMode, on ? '1' : '0');
      push({ darkMode: on });
    },
    [push],
  );

  // Closes over `darkMode` rather than using a setState updater: the updater
  // form would put the storage write and the network push inside a function
  // React is free to call twice (StrictMode does exactly that in development),
  // which would fire two mutations per click. The component re-renders on every
  // change, so the captured value is never stale.
  const toggleDarkMode = useCallback(
    () => setDarkMode(!darkMode),
    [darkMode, setDarkMode],
  );

  // Memoised — this provider wraps the whole app, so an unstable value would
  // re-render every screen on each render of the provider.
  const value = useMemo(
    () => ({ language, darkMode, setLanguage, setDarkMode, toggleDarkMode }),
    [language, darkMode, setLanguage, setDarkMode, toggleDarkMode],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextValue => useContext(PreferencesContext);

/**
 * Narrow accessor for the language alone — what useT() needs. Kept separate so
 * a component that only translates doesn't re-render when dark mode changes…
 * except it still does, because they share one context value. It stays for
 * readability at the call site, matching mobileapp's useLanguage().
 */
export const useLanguage = (): { language: Language; setLanguage: (l: Language) => void } => {
  const { language, setLanguage } = usePreferences();
  return { language, setLanguage };
};

/**
 * Dark mode alone, named after mobileapp/src/theme/useAppTheme.ts. The mobile
 * version also returns a resolved `Colors` object; here the palette lives in
 * CSS custom properties (see styles/style.css), so components need the flag
 * only for the rare case that CSS can't express — e.g. handing a theme to
 * ag-grid.
 */
export const useAppTheme = () => {
  const { darkMode, toggleDarkMode } = usePreferences();
  return {
    isDarkMode: darkMode,
    theme: darkMode ? ('dark' as const) : ('light' as const),
    toggleDarkMode,
  };
};
